class HealthMonitor {
  constructor() {
    this.metrics = {
      memory: {},
      display: {},
      content: {},
      network: {},
      errors: []
    };
    this.performanceObserver = null;
    this.errorCount = 0;
    this.contentLoadTimes = [];
    this.maxErrorsToStore = 10;
    this.maxLoadTimeSamples = 20;

    // Track intervals and listeners for proper cleanup
    this.memoryMonitorInterval = null;
    this._boundListeners = {
      error: null,
      unhandledrejection: null,
      online: null,
      offline: null,
      connectionChange: null
    };

    this.initializeMonitoring();
  }

  initializeMonitoring() {
    // Monitor memory usage
    this.startMemoryMonitoring();
    
    // Monitor performance
    this.startPerformanceMonitoring();
    
    // Monitor errors
    this.startErrorMonitoring();
    
    // Monitor network
    this.startNetworkMonitoring();
  }

  startMemoryMonitoring() {
    // Check if performance.memory is available (Chrome only)
    if (performance.memory) {
      // Clear any existing interval first
      if (this.memoryMonitorInterval) {
        clearInterval(this.memoryMonitorInterval);
      }
      this.memoryMonitorInterval = setInterval(() => {
        const memInfo = performance.memory;
        this.metrics.memory = {
          jsHeapSizeLimit: memInfo.jsHeapSizeLimit,
          totalJSHeapSize: memInfo.totalJSHeapSize,
          usedJSHeapSize: memInfo.usedJSHeapSize,
          usagePercent: ((memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100).toFixed(2)
        };
      }, 10000); // Check every 10 seconds
    }
  }

  startPerformanceMonitoring() {
    // Monitor navigation timing
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      
      this.metrics.performance = {
        pageLoadTime: loadTime,
        domContentLoadedTime: timing.domContentLoadedEventEnd - timing.navigationStart,
        responseTime: timing.responseEnd - timing.requestStart
      };
    }

    // Monitor resource loading
    if ('PerformanceObserver' in window) {
      try {
        this.performanceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'resource' && 
                (entry.name.includes('.mp4') || entry.name.includes('.webm') || 
                 entry.name.includes('youtube') || entry.name.includes('cloudinary'))) {
              this.recordContentLoadTime(entry.duration);
            }
          }
        });
        
        this.performanceObserver.observe({ entryTypes: ['resource'] });
      } catch (error) {
        console.error('Error setting up PerformanceObserver:', error);
      }
    }
  }

  startErrorMonitoring() {
    // Global error handler - store reference for cleanup
    this._boundListeners.error = (event) => {
      this.recordError({
        type: 'javascript',
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        timestamp: Date.now()
      });
    };
    window.addEventListener('error', this._boundListeners.error);

    // Unhandled promise rejection handler - store reference for cleanup
    this._boundListeners.unhandledrejection = (event) => {
      this.recordError({
        type: 'unhandledRejection',
        message: event.reason?.toString() || 'Unknown rejection',
        timestamp: Date.now()
      });
    };
    window.addEventListener('unhandledrejection', this._boundListeners.unhandledrejection);
  }

  startNetworkMonitoring() {
    // Monitor online/offline status
    this.metrics.network.online = navigator.onLine;

    // Store listener references for cleanup
    this._boundListeners.online = () => {
      this.metrics.network.online = true;
      this.metrics.network.lastOnline = Date.now();
    };
    window.addEventListener('online', this._boundListeners.online);

    this._boundListeners.offline = () => {
      this.metrics.network.online = false;
      this.metrics.network.lastOffline = Date.now();
    };
    window.addEventListener('offline', this._boundListeners.offline);

    // Monitor connection type if available
    if ('connection' in navigator) {
      const connection = navigator.connection;
      this.metrics.network.connectionType = connection.effectiveType;
      this.metrics.network.downlink = connection.downlink;
      this.metrics.network.rtt = connection.rtt;

      // Store connection change listener reference for cleanup
      this._boundListeners.connectionChange = () => {
        this.metrics.network.connectionType = connection.effectiveType;
        this.metrics.network.downlink = connection.downlink;
        this.metrics.network.rtt = connection.rtt;
      };
      connection.addEventListener('change', this._boundListeners.connectionChange);
    }
  }

  recordContentLoadTime(duration) {
    this.contentLoadTimes.push({
      duration,
      timestamp: Date.now()
    });
    
    // Keep only recent samples
    if (this.contentLoadTimes.length > this.maxLoadTimeSamples) {
      this.contentLoadTimes.shift();
    }
    
    // Calculate average load time
    const avgLoadTime = this.contentLoadTimes.reduce((sum, item) => sum + item.duration, 0) / this.contentLoadTimes.length;
    this.metrics.content.averageLoadTime = avgLoadTime;
    this.metrics.content.lastLoadTime = duration;
  }

  recordError(error) {
    this.errorCount++;
    this.metrics.errors.push(error);
    
    // Keep only recent errors
    if (this.metrics.errors.length > this.maxErrorsToStore) {
      this.metrics.errors.shift();
    }
  }

  checkDisplayStatus() {
    // Check if document is visible
    this.metrics.display.isVisible = !document.hidden;
    this.metrics.display.visibilityState = document.visibilityState;
    
    // Check screen dimensions
    this.metrics.display.screenWidth = window.screen.width;
    this.metrics.display.screenHeight = window.screen.height;
    this.metrics.display.windowWidth = window.innerWidth;
    this.metrics.display.windowHeight = window.innerHeight;
    
    // Check if fullscreen
    this.metrics.display.isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    
    return this.metrics.display;
  }

  checkContentStatus() {
    // Check video elements
    const videos = document.querySelectorAll('video');
    const videoStatuses = Array.from(videos).map((video, index) => ({
      index,
      playing: !video.paused,
      currentTime: video.currentTime,
      duration: video.duration,
      readyState: video.readyState,
      networkState: video.networkState,
      error: video.error?.message || null
    }));
    
    this.metrics.content.videos = videoStatuses;
    this.metrics.content.activeVideos = videoStatuses.filter(v => v.playing).length;
    this.metrics.content.totalVideos = videoStatuses.length;
    
    // Check images
    const images = document.querySelectorAll('img');
    const imageStatuses = Array.from(images).map((img) => ({
      loaded: img.complete && img.naturalHeight !== 0,
      src: img.src
    }));
    
    this.metrics.content.images = {
      total: imageStatuses.length,
      loaded: imageStatuses.filter(i => i.loaded).length,
      failed: imageStatuses.filter(i => !i.loaded).length
    };
    
    return this.metrics.content;
  }

  async collectHealthMetrics() {
    // Update all metrics
    this.checkDisplayStatus();
    this.checkContentStatus();
    
    const health = {
      memory: this.metrics.memory,
      display: this.metrics.display,
      content: {
        ...this.metrics.content,
        errorCount: this.errorCount,
        recentErrors: this.metrics.errors.slice(-3) // Last 3 errors
      },
      network: this.metrics.network,
      performance: this.metrics.performance,
      timestamp: Date.now()
    };
    
    return health;
  }

  async generateFullReport(checks = ['memory', 'display', 'content', 'network']) {
    const report = {
      timestamp: Date.now(),
      checks: {}
    };
    
    if (checks.includes('memory')) {
      report.checks.memory = this.metrics.memory;
    }
    
    if (checks.includes('display')) {
      report.checks.display = this.checkDisplayStatus();
    }
    
    if (checks.includes('content')) {
      report.checks.content = this.checkContentStatus();
    }
    
    if (checks.includes('network')) {
      report.checks.network = this.metrics.network;
    }
    
    if (checks.includes('performance')) {
      report.checks.performance = this.metrics.performance;
    }
    
    if (checks.includes('errors')) {
      report.checks.errors = {
        count: this.errorCount,
        recent: this.metrics.errors
      };
    }
    
    return report;
  }

  getLatestMetrics() {
    return {
      memory: this.metrics.memory,
      display: this.metrics.display,
      content: this.metrics.content,
      network: this.metrics.network,
      performance: this.metrics.performance,
      errorCount: this.errorCount,
      lastUpdated: Date.now()
    };
  }

  resetErrors() {
    this.metrics.errors = [];
    this.errorCount = 0;
  }

  destroy() {
    // Clean up memory monitoring interval
    if (this.memoryMonitorInterval) {
      clearInterval(this.memoryMonitorInterval);
      this.memoryMonitorInterval = null;
    }

    // Clean up performance observer
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }

    // Clean up error listeners
    if (this._boundListeners.error) {
      window.removeEventListener('error', this._boundListeners.error);
      this._boundListeners.error = null;
    }
    if (this._boundListeners.unhandledrejection) {
      window.removeEventListener('unhandledrejection', this._boundListeners.unhandledrejection);
      this._boundListeners.unhandledrejection = null;
    }

    // Clean up network listeners
    if (this._boundListeners.online) {
      window.removeEventListener('online', this._boundListeners.online);
      this._boundListeners.online = null;
    }
    if (this._boundListeners.offline) {
      window.removeEventListener('offline', this._boundListeners.offline);
      this._boundListeners.offline = null;
    }
    if (this._boundListeners.connectionChange && 'connection' in navigator) {
      navigator.connection.removeEventListener('change', this._boundListeners.connectionChange);
      this._boundListeners.connectionChange = null;
    }

    // Clear metrics to free memory
    this.metrics = {
      memory: {},
      display: {},
      content: {},
      network: {},
      errors: []
    };
    this.contentLoadTimes = [];
    this.errorCount = 0;
  }
}

export default HealthMonitor;